import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Editor, { OnMount, OnChange } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import {
  Button,
  InlineStack,
  Text,
  Tooltip,
  Spinner,
  Badge,
  Icon,
  Banner,
  Popover,
  ActionList,
} from '@shopify/polaris';
import {
  AlertTriangleIcon,
  CheckIcon,
  CodeIcon,
  RefreshIcon,
  SendIcon,
  SunIcon,
  MoonIcon,
  PlusIcon,
} from '@shopify/polaris-icons';
import { useAiTemplateEditor } from '../hooks/useAiTemplateEditor';

// Workaround for Monaco Editor React 18 type compatibility
const MonacoEditor = Editor as any;

interface AiTemplateEditorProps {
  value: string;
  onChange: (value: string) => void;
  templateType?: 'abandoned_cart' | 'post_purchase';
  height?: string;
  readOnly?: boolean;
  theme?: 'vs-dark' | 'vs-light';
  onThemeChange?: (theme: 'vs-dark' | 'vs-light') => void;
  availableVariables?: string[];
}

interface ValidationError {
  line: number;
  column: number;
  message: string;
  severity: 'error' | 'warning';
}

// Suggested AI prompts for quick actions
const QUICK_PROMPTS = {
  abandoned_cart: [
    { label: '🎨 More colorful', prompt: 'Make the template more visually colorful and vibrant, using a gradient or bright accent colors' },
    { label: '✨ Add urgency', prompt: 'Add an urgency banner at the top with a countdown-style element' },
    { label: '🛡️ Add trust badges', prompt: 'Add trust badges section with secure checkout, free shipping, and easy returns icons' },
    { label: '📱 Simplify for mobile', prompt: 'Simplify the layout to be more mobile-friendly, with larger buttons and less clutter' },
  ],
  post_purchase: [
    { label: '🎉 More celebratory', prompt: 'Make the template more celebratory and thankful with warm colors and confetti-style elements' },
    { label: '⭐ Add review request', prompt: 'Add a section asking customers to leave a review after they receive their order' },
    { label: '🎁 Add referral CTA', prompt: 'Add a referral section where customers can share with friends for a discount' },
    { label: '📱 Simplify for mobile', prompt: 'Simplify the layout to be more mobile-friendly, with larger buttons and less clutter' },
  ],
};

export default function AiTemplateEditor({
  value,
  onChange,
  templateType = 'abandoned_cart',
  readOnly = false,
  theme = 'vs-dark',
  onThemeChange,
  availableVariables = [],
}: AiTemplateEditorProps) {
  const { t } = useTranslation();
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  
  // Local state
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ line: 1, column: 1 });
  const [showAiPanel, setShowAiPanel] = useState(false); // Start collapsed to give more editor space
  const [aiPrompt, setAiPrompt] = useState('');
  const [variablesPopoverActive, setVariablesPopoverActive] = useState(false);
  
  // AI hook
  const { editWithAi, fetchUsage, isLoading: isAiLoading, error: aiError, usage, clearError } = useAiTemplateEditor({
    templateType,
  });

  // Fetch AI usage on mount
  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // Default template variables
  const templateVariables = availableVariables.length > 0 ? availableVariables : [
    '{{customer_name}}',
    '{{shop_name}}',
    '{{primary_color}}',
    '{{background_color}}',
    '{{text_color}}',
    '{{recommendations}}',
    '{{bundles}}',
    '{{cart_items}}',
    '{{order_items}}',
    '{{cart_url}}',
    '{{footer_text}}',
  ];

  // HTML validation function
  const validateHtml = useCallback((html: string): ValidationError[] => {
    const validationErrors: ValidationError[] = [];
    const lines = html.split('\n');
    
    const tagStack: { tag: string; line: number }[] = [];
    const selfClosingTags = ['img', 'br', 'hr', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'param', 'source', 'track', 'wbr'];
    
    lines.forEach((line, lineIndex) => {
      const lineNum = lineIndex + 1;
      
      const quoteMatches = line.match(/['"]/g);
      if (quoteMatches && quoteMatches.length % 2 !== 0) {
        if (!line.includes('{{') && !line.includes('}}')) {
          validationErrors.push({
            line: lineNum,
            column: line.lastIndexOf('"') + 1 || line.lastIndexOf("'") + 1,
            message: 'Possible unclosed quote in attribute',
            severity: 'warning',
          });
        }
      }
      
      const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*\/?>/g;
      let match;
      
      while ((match = tagRegex.exec(line)) !== null) {
        const fullMatch = match[0];
        const tagName = match[1].toLowerCase();
        const isClosing = fullMatch.startsWith('</');
        const isSelfClosing = fullMatch.endsWith('/>') || selfClosingTags.includes(tagName);
        
        if (isClosing) {
          const lastOpen = [...tagStack].reverse().findIndex(t => t.tag === tagName);
          if (lastOpen === -1) {
            validationErrors.push({
              line: lineNum,
              column: match.index + 1,
              message: `Closing tag </${tagName}> without matching opening tag`,
              severity: 'error',
            });
          } else {
            const removeIndex = tagStack.length - 1 - lastOpen;
            tagStack.splice(removeIndex);
          }
        } else if (!isSelfClosing) {
          tagStack.push({ tag: tagName, line: lineNum });
        }
      }
    });
    
    return validationErrors;
  }, []);

  // Debounced validation
  useEffect(() => {
    setIsValidating(true);
    const timer = setTimeout(() => {
      const validationErrors = validateHtml(value);
      setErrors(validationErrors);
      setIsValidating(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [value, validateHtml]);

  // Editor mount handler
  const handleEditorDidMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    
    // Configure HTML language features
    monaco.languages.html.htmlDefaults.setOptions({
      format: {
        tabSize: 2,
        insertSpaces: true,
        wrapLineLength: 120,
        wrapAttributes: 'auto',
        preserveNewLines: true,
        maxPreserveNewLines: 2,
      },
      suggest: {
        html5: true,
      },
    });
    
    // Add template variable completions
    monaco.languages.registerCompletionItemProvider('html', {
      provideCompletionItems: (model: any, position: any) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };
        
        const suggestions = templateVariables.map((variable) => ({
          label: variable,
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: variable,
          range: range,
          detail: 'Template variable',
          documentation: `Insert ${variable} placeholder`,
        }));
        
        return { suggestions };
      },
      triggerCharacters: ['{'],
    });
    
    // Track cursor position
    editor.onDidChangeCursorPosition((e: any) => {
      setCursorPosition({
        line: e.position.lineNumber,
        column: e.position.column,
      });
    });
  }, [templateVariables]);

  // Editor change handler
  const handleEditorChange: OnChange = useCallback((newValue) => {
    if (newValue !== undefined) {
      onChange(newValue);
    }
  }, [onChange]);

  // Format document
  const formatDocument = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.getAction('editor.action.formatDocument')?.run();
    }
  }, []);

  // Insert variable at cursor
  const insertVariable = useCallback((variable: string) => {
    if (editorRef.current) {
      const editor = editorRef.current;
      const position = editor.getPosition();
      if (position) {
        editor.executeEdits('insert-variable', [{
          range: {
            startLineNumber: position.lineNumber,
            startColumn: position.column,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          },
          text: variable,
        }]);
        editor.focus();
      }
    }
  }, []);

  // Handle AI edit
  const handleAiEdit = useCallback(async (prompt: string) => {
    if (!prompt.trim()) return;
    
    const result = await editWithAi(prompt, value);
    if (result?.html) {
      onChange(result.html);
      setAiPrompt('');
    }
  }, [editWithAi, value, onChange]);

  // Handle quick prompt
  const handleQuickPrompt = useCallback((prompt: string) => {
    handleAiEdit(prompt);
  }, [handleAiEdit]);

  // Toggle theme
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'vs-dark' ? 'vs-light' : 'vs-dark';
    onThemeChange?.(newTheme);
  }, [theme, onThemeChange]);

  const errorCount = errors.filter(e => e.severity === 'error').length;
  const warningCount = errors.filter(e => e.severity === 'warning').length;
  const quickPrompts = QUICK_PROMPTS[templateType] || QUICK_PROMPTS.abandoned_cart;
  
  const isDarkTheme = theme === 'vs-dark';
  const bgColor = isDarkTheme ? '#1e1e1e' : '#ffffff';
  const borderColor = isDarkTheme ? '#333' : '#e1e3e5';
  const textColor = isDarkTheme ? '#fff' : '#202223';
  const secondaryColor = isDarkTheme ? '#888' : '#6d7175';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Main Toolbar */}
      <div style={{ 
        padding: '8px 12px', 
        backgroundColor: isDarkTheme ? '#252526' : '#f6f6f7',
        borderBottom: `1px solid ${borderColor}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <InlineStack gap="300" blockAlign="center">
          <InlineStack gap="100" blockAlign="center">
            <Icon source={CodeIcon} tone={isDarkTheme ? 'base' : 'base'} />
            <Text as="span" variant="bodySm" fontWeight="semibold">
              <span style={{ color: textColor }}>HTML Editor</span>
            </Text>
          </InlineStack>
          
          {/* Status indicators */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isValidating ? (
              <Spinner size="small" />
            ) : errorCount > 0 || warningCount > 0 ? (
              <>
                {errorCount > 0 && (
                  <Tooltip content={`${errorCount} error${errorCount > 1 ? 's' : ''}`}>
                    <span style={{ color: '#f87171', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon source={AlertTriangleIcon} />
                      <span style={{ fontSize: '12px' }}>{errorCount}</span>
                    </span>
                  </Tooltip>
                )}
                {warningCount > 0 && (
                  <Tooltip content={`${warningCount} warning${warningCount > 1 ? 's' : ''}`}>
                    <span style={{ color: '#fbbf24', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon source={AlertTriangleIcon} />
                      <span style={{ fontSize: '12px' }}>{warningCount}</span>
                    </span>
                  </Tooltip>
                )}
              </>
            ) : (
              <Tooltip content="No issues found">
                <span style={{ color: '#4ade80', display: 'flex', alignItems: 'center' }}>
                  <Icon source={CheckIcon} />
                </span>
              </Tooltip>
            )}
          </div>
        </InlineStack>
        
        <InlineStack gap="200">
          <Tooltip content={isDarkTheme ? t('templateEditor.lightTheme', 'Light theme') : t('templateEditor.darkTheme', 'Dark theme')}>
            <Button 
              size="micro" 
              icon={isDarkTheme ? SunIcon : MoonIcon}
              onClick={toggleTheme}
              variant="tertiary"
            />
          </Tooltip>
          <Button 
            size="micro" 
            icon={RefreshIcon}
            onClick={formatDocument}
            variant="tertiary"
          >
            Format
          </Button>
        </InlineStack>
      </div>
      
      {/* Variable insertion bar */}
      <div style={{ 
        padding: '6px 12px', 
        backgroundColor: isDarkTheme ? '#2d2d2d' : '#f0f1f2',
        borderBottom: `1px solid ${borderColor}`,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'nowrap',
        overflow: 'hidden',
      }}>
        <Text as="span" variant="bodySm">
          <span style={{ color: secondaryColor, whiteSpace: 'nowrap' }}>{t('templateEditor.insertVariable', 'Insert:')}</span>
        </Text>
        {templateVariables.slice(0, 5).map((variable) => (
          <button
            key={variable}
            onClick={() => insertVariable(variable)}
            style={{
              padding: '4px 8px',
              fontSize: '11px',
              backgroundColor: isDarkTheme ? '#0e639c' : '#0284c7',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontFamily: 'monospace',
              whiteSpace: 'nowrap',
            }}
          >
            {variable}
          </button>
        ))}
        {templateVariables.length > 5 && (
          <Popover
            active={variablesPopoverActive}
            activator={
              <button
                onClick={() => setVariablesPopoverActive(!variablesPopoverActive)}
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  backgroundColor: isDarkTheme ? '#374151' : '#e5e7eb',
                  color: isDarkTheme ? '#9ca3af' : '#374151',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Icon source={PlusIcon} />
                <span>{templateVariables.length - 5} more</span>
              </button>
            }
            onClose={() => setVariablesPopoverActive(false)}
            preferredPosition="below"
          >
            <ActionList
              items={templateVariables.slice(5).map((variable) => ({
                content: variable,
                onAction: () => {
                  insertVariable(variable);
                  setVariablesPopoverActive(false);
                },
              }))}
            />
          </Popover>
        )}
      </div>
      
      {/* AI Assistant - Compact inline bar */}
      <div style={{
        padding: '6px 12px',
        backgroundColor: isDarkTheme ? '#1a1a2e' : '#f0f7ff',
        borderBottom: `1px solid ${borderColor}`,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
      }}>
        <InlineStack gap="200" blockAlign="center">
          <span style={{ fontSize: '14px' }}>✨</span>
          <Text as="span" variant="bodySm" fontWeight="semibold">
            <span style={{ color: textColor }}>AI:</span>
          </Text>
        </InlineStack>
        
        {/* Quick Actions inline */}
        {quickPrompts.map((qp, idx) => (
          <button
            key={idx}
            onClick={() => handleQuickPrompt(qp.prompt)}
            disabled={isAiLoading || (usage?.promptsRemaining ?? 0) <= 0}
            style={{
              padding: '4px 10px',
              fontSize: '11px',
              backgroundColor: isDarkTheme ? '#374151' : '#e0e7ff',
              color: isDarkTheme ? '#e5e7eb' : '#3730a3',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              opacity: isAiLoading || (usage?.promptsRemaining ?? 0) <= 0 ? 0.5 : 1,
            }}
          >
            {qp.label}
          </button>
        ))}
        
        {/* Custom prompt toggle */}
        <button
          onClick={() => setShowAiPanel(!showAiPanel)}
          style={{
            padding: '4px 10px',
            fontSize: '11px',
            backgroundColor: isDarkTheme ? '#4c1d95' : '#ddd6fe',
            color: isDarkTheme ? '#c4b5fd' : '#5b21b6',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          ✏️ {t('templateEditor.customPrompt', 'Custom')}
        </button>
        
        {usage && (
          <Badge size="small" tone={usage.promptsRemaining > 10 ? 'success' : usage.promptsRemaining > 0 ? 'warning' : 'critical'}>
            {`${usage.promptsRemaining}/${usage.dailyLimit}`}
          </Badge>
        )}
        
        {isAiLoading && <Spinner size="small" />}
      </div>
      
      {/* Custom prompt input - only shown when expanded */}
      {showAiPanel && (
        <div style={{
          padding: '8px 12px',
          backgroundColor: isDarkTheme ? '#1a1a2e' : '#f0f7ff',
          borderBottom: `1px solid ${borderColor}`,
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
        }}>
          {aiError && (
            <Banner tone="critical" onDismiss={clearError}>
              {aiError}
            </Banner>
          )}
          <div style={{ flex: 1 }}>
            <input
              type="text"
              placeholder={t('templateEditor.aiPromptPlaceholder', 'Describe what changes you want...')}
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAiEdit(aiPrompt)}
              disabled={isAiLoading || (usage?.promptsRemaining ?? 0) <= 0}
              style={{
                width: '100%',
                padding: '6px 10px',
                fontSize: '13px',
                border: `1px solid ${borderColor}`,
                borderRadius: '4px',
                backgroundColor: isDarkTheme ? '#2d2d2d' : '#fff',
                color: textColor,
                outline: 'none',
              }}
            />
          </div>
          <Button
            icon={SendIcon}
            onClick={() => handleAiEdit(aiPrompt)}
            loading={isAiLoading}
            disabled={!aiPrompt.trim() || (usage?.promptsRemaining ?? 0) <= 0}
            variant="primary"
            size="slim"
          >
            {t('templateEditor.generate', 'Generate')}
          </Button>
        </div>
      )}
      
      {/* Monaco Editor */}
      <div style={{ flex: 1, minHeight: '300px' }}>
        <MonacoEditor
          height="100%"
          defaultLanguage="html"
          value={value}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          theme={theme}
          options={{
            readOnly,
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: 'on',
            wordWrap: 'on',
            automaticLayout: true,
            scrollBeyondLastLine: false,
            tabSize: 2,
            insertSpaces: true,
            formatOnPaste: true,
            bracketPairColorization: { enabled: true },
            suggest: {
              showClasses: true,
              showColors: true,
              showConstants: true,
              showFunctions: true,
              showKeywords: true,
              showMethods: true,
              showProperties: true,
              showSnippets: true,
              showVariables: true,
            },
            quickSuggestions: {
              other: true,
              comments: false,
              strings: true,
            },
            folding: true,
            foldingHighlight: true,
            foldingStrategy: 'auto',
            showFoldingControls: 'mouseover',
            matchBrackets: 'always',
            renderWhitespace: 'selection',
            scrollbar: {
              vertical: 'auto',
              horizontal: 'auto',
              useShadows: false,
              verticalScrollbarSize: 10,
              horizontalScrollbarSize: 10,
            },
          }}
          loading={
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              backgroundColor: bgColor,
              color: secondaryColor,
            }}>
              <InlineStack gap="200" blockAlign="center">
                <Spinner size="small" />
                <Text as="span">{t('templateEditor.loadingEditor', 'Loading editor...')}</Text>
              </InlineStack>
            </div>
          }
        />
      </div>
      
      {/* Status bar */}
      <div style={{ 
        padding: '4px 12px', 
        backgroundColor: isDarkTheme ? '#007acc' : '#0284c7',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '12px',
        color: '#fff',
      }}>
        <span>Ln {cursorPosition.line}, Col {cursorPosition.column}</span>
        <InlineStack gap="300">
          <span>HTML</span>
          <span>{theme === 'vs-dark' ? '🌙' : '☀️'}</span>
        </InlineStack>
      </div>
      
      {/* Error panel */}
      {errors.length > 0 && (
        <div style={{ 
          maxHeight: '100px', 
          overflow: 'auto', 
          backgroundColor: bgColor,
          borderTop: `1px solid ${borderColor}`,
        }}>
          {errors.slice(0, 5).map((error, index) => (
            <div 
              key={index}
              style={{ 
                padding: '6px 12px', 
                fontSize: '12px',
                color: error.severity === 'error' ? '#f87171' : '#fbbf24',
                cursor: 'pointer',
                borderBottom: `1px solid ${borderColor}`,
              }}
              onClick={() => {
                if (editorRef.current) {
                  editorRef.current.revealLineInCenter(error.line);
                  editorRef.current.setPosition({ lineNumber: error.line, column: error.column });
                  editorRef.current.focus();
                }
              }}
            >
              <span style={{ marginRight: '8px' }}>
                {error.severity === 'error' ? '❌' : '⚠️'}
              </span>
              Line {error.line}: {error.message}
            </div>
          ))}
          {errors.length > 5 && (
            <div style={{ padding: '6px 12px', fontSize: '12px', color: secondaryColor }}>
              ... and {errors.length - 5} more issues
            </div>
          )}
        </div>
      )}
    </div>
  );
}
